import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  CloseButton,
  Dialog,
  Flex,
  HStack,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import type { FormErrors } from '@customTypes/errors';
import ErrorLabel from '@components/ErrorLabel';
import Loading from '@components/Loading';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';
import DualListPanel from '@components/DualListPanel';
import {
  DEFAULT_DUAL_LIST_LABELS,
  type DualListItem,
  type DualListLabels,
} from '@utils/dualListSelect';

export type { DualListItem, DualListLabels };

export interface DualListSelectDialogProps {
  onClose: () => void;
  title: string;
  subtitle?: React.ReactNode;
  items: DualListItem[];
  initialSelectedIds: string[];
  onSave: (added: string[], removed: string[]) => Promise<void>;
  loading?: boolean;
  errors?: FormErrors | null;
  labels?: DualListLabels;
}

interface ContentProps {
  items: DualListItem[];
  initialSelectedIds: string[];
  onSave: (added: string[], removed: string[]) => Promise<void>;
  onClose: () => void;
  errors?: FormErrors | null;
  labels: Required<DualListLabels>;
}

const DualListContent = ({ items, initialSelectedIds, onSave, onClose, errors, labels }: ContentProps) => {
  const [saving, setSaving] = useState(false);
  const [draftSelectedIds, setDraftSelectedIds] = useState<string[]>(initialSelectedIds);

  const initialSet = useMemo(() => new Set(initialSelectedIds), [initialSelectedIds]);
  const selectedSet = useMemo(() => new Set(draftSelectedIds), [draftSelectedIds]);

  const addedCount = useMemo(
    () => draftSelectedIds.filter((id) => !initialSet.has(id)).length,
    [draftSelectedIds, initialSet]
  );

  const removedCount = useMemo(
    () => [...initialSet].filter((id) => !selectedSet.has(id)).length,
    [initialSet, selectedSet]
  );

  const handleSave = async () => {
    setSaving(true);
    const added = draftSelectedIds.filter((id) => !initialSet.has(id));
    const removed = [...initialSet].filter((id) => !selectedSet.has(id));
    try {
      await onSave(added, removed);
    } catch {
      // onSave is responsible for showing error UI
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog.Body pt="0" flex="1" display="flex" flexDir="column" overflow={{ base: 'auto', lg: 'hidden' }} minH="0">
        <Stack gap="5" flex="1" minH="0">
          <DualListPanel
            items={items}
            selectedIds={draftSelectedIds}
            onChange={setDraftSelectedIds}
            labels={labels}
            extraBadges={
              <>
                {addedCount > 0 && (
                  <Badge colorPalette="green" variant="subtle" rounded="full" px="3" py="1">
                    +{addedCount} adicionados
                  </Badge>
                )}
                {removedCount > 0 && (
                  <Badge colorPalette="red" variant="subtle" rounded="full" px="3" py="1">
                    -{removedCount} removidos
                  </Badge>
                )}
              </>
            }
          />

          {errors?.general && (
            <Stack gap="1">
              {errors.general.map((error, index) => (
                <ErrorLabel key={index} text={error} />
              ))}
            </Stack>
          )}
        </Stack>
      </Dialog.Body>

      <Dialog.Footer pt="5">
        <HStack justify="space-between" w="full" flexWrap="wrap" gap="3">
          <Text fontSize="sm" color="fg.muted">{labels.footerTip}</Text>
          <HStack>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              {labels.cancel}
            </Button>
            <Button bg="green.700" color="white" onClick={handleSave} loading={saving}>
              {labels.save}
            </Button>
          </HStack>
        </HStack>
      </Dialog.Footer>
    </>
  );
};

const DualListSelectDialog = ({
  onClose,
  title,
  subtitle,
  items,
  initialSelectedIds,
  onSave,
  loading = false,
  errors,
  labels,
}: DualListSelectDialogProps) => {
  const resolvedLabels = { ...DEFAULT_DUAL_LIST_LABELS, ...labels };

  return (
    <Dialog.Root
      size="xl"
      placement="center"
      open
      onOpenChange={onClose}
      closeOnInteractOutside={false}
      motionPreset="slide-in-bottom"
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="1100px"
            overflow="hidden"
            bg="grayscale.fg"
            display="flex"
            flexDir="column"
            maxH={{ base: '100dvh', lg: 'calc(100dvh - 2rem)' }}
          >
            <Dialog.Header pb="4">
              <Flex align="start" justify="space-between" gap="4" w="full">
                <Stack gap="1">
                  <Dialog.Title fontSize="xl" fontWeight="semibold">{title}</Dialog.Title>
                  {subtitle}
                </Stack>
                <Dialog.CloseTrigger asChild>
                  <CloseButton size="sm" />
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Header>

            {loading ? (
              <Dialog.Body>
                <Loading />
              </Dialog.Body>
            ) : (
              <DualListContent
                items={items}
                initialSelectedIds={initialSelectedIds}
                onSave={onSave}
                onClose={onClose}
                errors={errors}
                labels={resolvedLabels}
              />
            )}
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default DualListSelectDialog;
